import {
	Component,
	computed,
	input,
	model,
	output,
	signal,
	ViewEncapsulation,
} from '@angular/core';

import { SelectShellComponent } from '@valk-nx/components/ui-select-shell/src/lib/select-shell';
import type { DropdownOption } from '@valk-nx/components/ui-select-shell/src/lib/select-shell.interface';

@Component({
	encapsulation: ViewEncapsulation.None,
	exportAs: 'vpAutocomplete',
	// In manual open mode ng-select ignores Escape, so the panel closes here
	host: { '(keydown.escape)': 'closePanel()' },
	imports: [SelectShellComponent],
	selector: 'vp-autocomplete',
	styleUrls: ['./scss/autocomplete.scss'],
	templateUrl: './autocomplete.html',
})
export class AutocompleteComponent {
	options = input.required<DropdownOption[]>();
	placeholder = input<string>('components.dropdown.placeholder');
	notFoundText = input<string>('global.no-results-found');
	loadingText = input<string>('global.loading.title');
	inputId = input<string>('');
	label = input<string>('');
	loading = input<boolean>(false);
	// Below this the panel stays closed, so no premature "no results" shows
	// while the caller is not searching yet. Must be at least 1: the panel
	// only opens once something is typed, never on click.
	minimumCharacters = input<number>(1);

	value = model<string | null>(null);

	searched = output<string>();
	selectedValue = output<string>();

	// The panel only opens once something is typed, never on click
	readonly panelOpen = signal(false);

	// options only hold the current search results, so the picked option is
	// remembered to keep its label when new results come in
	private readonly selectedOption = signal<DropdownOption | null>(null);

	selectedLabel = computed(() => {
		const selected = this.selectedOption();
		if (selected?.value === this.value()) {
			return selected.label;
		}
		return (
			this.options().find((option) => option.value === this.value())
				?.label ??
			this.value() ??
			''
		);
	});

	closePanel(): void {
		this.panelOpen.set(false);
	}

	onCleared(): void {
		this.panelOpen.set(false);
		this.searched.emit('');
	}

	onSearched(term: string): void {
		if (!term) {
			this.value.set(null);
			this.panelOpen.set(false);
		} else {
			this.panelOpen.set(term.length >= this.minimumCharacters());
		}
		this.searched.emit(term);
	}

	update(): void {
		this.panelOpen.set(false);
		this.selectedOption.set(
			this.options().find((option) => option.value === this.value()) ??
				null,
		);
		this.selectedValue.emit(this.value() ?? '');
	}
}
